import React, { useEffect, useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

interface TargetSettings {
  catchAngleMin: number;
  catchAngleMax: number;
  finishAngleMin: number;
  finishAngleMax: number;
  catchGapMax: number;
  finishSlipMin: number;
  finishSlipMax: number;
  warningBand: number;
}

interface TargetSettingsPanelProps {
  targets: TargetSettings;
  onTargetChange: (field: keyof TargetSettings, value: number) => void;
  onResetToDefaults?: () => void;
  validationMessages?: string[];
}

function formatTargetDisplay(n: number): string {
  return Number.isFinite(n) ? n.toFixed(1) : '0.0';
}

interface TargetNumericFieldProps {
  label: string;
  field: keyof TargetSettings;
  value: number;
  unit?: string;
  step?: number;
  onCommit: (field: keyof TargetSettings, value: number) => void;
}

const TargetNumericField: React.FC<TargetNumericFieldProps> = ({
  label,
  field,
  value,
  unit = '°',
  step = 0.1,
  onCommit,
}) => {
  const [draft, setDraft] = useState(() => formatTargetDisplay(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setDraft(formatTargetDisplay(value));
    }
  }, [value, focused]);

  const readBaseForStep = (): number => {
    if (focused && draft.trim() !== '') {
      const parsed = parseFloat(draft);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
    return value;
  };

  const applyStep = (delta: number) => {
    const next = Math.max(0, readBaseForStep() + delta);
    onCommit(field, next);
    setDraft(formatTargetDisplay(next));
  };

  const handleBlur = () => {
    setFocused(false);
    const trimmed = draft.trim();
    if (trimmed === '' || trimmed === '-' || trimmed === '.' || trimmed === '-.') {
      setDraft(formatTargetDisplay(value));
      return;
    }
    const parsed = parseFloat(trimmed);
    if (Number.isNaN(parsed)) {
      setDraft(formatTargetDisplay(value));
      return;
    }
    onCommit(field, parsed);
  };

  const controlClass =
    'px-2 py-1.5 hover:bg-rowing-gray-50 active:bg-rowing-gray-100 border-r border-rowing-gray-200 rounded-l-md transition-colors cursor-pointer';
  const controlClassRight =
    'px-2 py-1.5 hover:bg-rowing-gray-50 active:bg-rowing-gray-100 border-l border-rowing-gray-200 rounded-r-md transition-colors cursor-pointer';

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-rowing-gray-600">{label}</label>
      <div className="flex items-center border border-rowing-gray-200 rounded-md bg-white hover:border-rowing-blue transition-colors focus-within:border-rowing-blue focus-within:ring-2 focus-within:ring-rowing-blue/10">
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            applyStep(-step);
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
            applyStep(-step);
          }}
          className={controlClass}
          aria-label={`Decrease ${label}`}
        >
          <ChevronDown size={14} className="text-rowing-gray-600" />
        </button>
        <div className="flex items-center flex-1 min-w-0">
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            aria-label={label}
            value={draft}
            onFocus={() => setFocused(true)}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={handleBlur}
            className="w-full min-w-0 px-2 py-1.5 text-sm text-center border-0 focus:outline-none bg-transparent text-rowing-gray-900 font-medium"
          />
          {unit && (
            <span className="text-sm text-rowing-gray-500 px-2 flex-shrink-0 border-l border-rowing-gray-200">
              {unit}
            </span>
          )}
        </div>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            applyStep(step);
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
            applyStep(step);
          }}
          className={controlClassRight}
          aria-label={`Increase ${label}`}
        >
          <ChevronUp size={14} className="text-rowing-gray-600" />
        </button>
      </div>
    </div>
  );
};

const TargetSettingsPanel: React.FC<TargetSettingsPanelProps> = ({
  targets,
  onTargetChange,
  onResetToDefaults,
  validationMessages = [],
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="rounded-lg border border-border bg-surface shadow-sm">
      {/* Collapsible Header */}
      <button
        type="button"
        className="w-full p-4 cursor-pointer border-b border-rowing-gray-100 text-left focus:outline-none focus:ring-2 focus:ring-inset focus-visible:ring-accent/50"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls="arc-target-settings-panel"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-rowing-gray-900">Target Settings</h3>
            <p className="text-xs text-rowing-gray-500 mt-0.5">
              Configure coach targets for metrics
            </p>
          </div>
          <ChevronDown
            className={`w-5 h-5 text-rowing-gray-500 transition-transform duration-200 ${
              isExpanded ? 'transform rotate-180' : ''
            }`}
          />
        </div>
      </button>

      {/* Collapsible Content */}
      <div
        id="arc-target-settings-panel"
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="p-3 sm:p-4">
          {onResetToDefaults && (
            <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={onResetToDefaults}
                className="text-xs font-medium text-rowing-blue hover:text-rowing-navy underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus-visible:ring-accent/50 rounded"
              >
                Reset to defaults
              </button>
            </div>
          )}
          {validationMessages.length > 0 && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2">
              {validationMessages.map((message) => (
                <p key={message} className="text-xs text-red-700">
                  {message}
                </p>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <section className="rounded-lg border border-rowing-gray-200 bg-rowing-gray-50 p-4">
              <div className="mb-3">
                <h4 className="text-sm font-semibold text-rowing-gray-900">Entry</h4>
                <p className="text-xs text-rowing-gray-500 mt-0.5">
                  Set the catch window and the maximum acceptable catch gap.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <TargetNumericField
                  label="Catch Angle Min"
                  field="catchAngleMin"
                  value={targets.catchAngleMin}
                  onCommit={onTargetChange}
                />
                <TargetNumericField
                  label="Catch Angle Max"
                  field="catchAngleMax"
                  value={targets.catchAngleMax}
                  onCommit={onTargetChange}
                />
                <TargetNumericField
                  label="Catch Gap Max"
                  field="catchGapMax"
                  value={targets.catchGapMax}
                  onCommit={onTargetChange}
                />
              </div>
            </section>

            <section className="rounded-lg border border-rowing-gray-200 bg-rowing-gray-50 p-4">
              <div className="mb-3">
                <h4 className="text-sm font-semibold text-rowing-gray-900">Exit</h4>
                <p className="text-xs text-rowing-gray-500 mt-0.5">
                  Set the finish window and the acceptable finish slip range.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <TargetNumericField
                  label="Finish Angle Min"
                  field="finishAngleMin"
                  value={targets.finishAngleMin}
                  onCommit={onTargetChange}
                />
                <TargetNumericField
                  label="Finish Angle Max"
                  field="finishAngleMax"
                  value={targets.finishAngleMax}
                  onCommit={onTargetChange}
                />
                <TargetNumericField
                  label="Finish Slip Max"
                  field="finishSlipMax"
                  value={targets.finishSlipMax}
                  onCommit={onTargetChange}
                />
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TargetSettingsPanel;
